import { createUseStyles } from 'react-jss';
import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    root: {
        flex: '1 1 0',
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        padding: '16px 24px 24px',
        gap: 0,
        boxSizing: 'border-box',
    },

    empty: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        fontFamily: theme.font.secondary,
        fontStyle: 'italic',
        fontSize: 20,
        color: theme.colors.grey_darker,
    },

    // ── Each event row ──────────────────────────────────────────────────
    eventRow: {
        display: 'flex',
        alignItems: 'stretch',
        position: 'relative',
        cursor: 'pointer',
        '&:last-child $connector': {
            display: 'none',
        },
    },

    // ── Timeline track (dot + vertical line) ───────────────────────────
    track: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        width: 52,
        marginRight: 12,
        paddingTop: 14,
    },

    dot: {
        width: 12,
        height: 12,
        borderRadius: '50%',
        flexShrink: 0,
        border: '2px solid',
        transition: 'transform 0.15s ease',
        '$eventRow:hover &': {
            transform: 'scale(1.3)',
        },
    },

    timeTag: {
        fontSize: 10,
        fontFamily: 'monospace',
        fontWeight: 700,
        letterSpacing: '0.03em',
        marginTop: 4,
        marginBottom: 3,
        textAlign: 'center' as const,
        whiteSpace: 'nowrap' as const,
        lineHeight: 1.2,
        opacity: theme.isDark ? 1 : 0.85,
    },

    connector: {
        flex: 1,
        width: 2,
        backgroundColor: theme.isDark ? theme.colors.grey_darker : theme.colors.grey_darker,
        opacity: theme.isDark ? 0.55 : 0.2,
        minHeight: 12,
    },

    // ── Card ────────────────────────────────────────────────────────────
    card: {
        flex: 1,
        minWidth: 0,
        backgroundColor: theme.isDark ? theme.colors.surface_elevated : theme.colors.white,
        borderRadius: 8,
        border: '1px solid',
        borderColor: theme.isDark ? theme.colors.grey_darker : theme.colors.grey_darker,
        marginBottom: 12,
        transition: 'box-shadow 0.18s ease, border-color 0.18s ease',
        overflow: 'hidden',
        '$eventRow:hover &': {
            boxShadow: theme.isDark
                ? '0 4px 20px rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.04)'
                : '0 4px 16px rgba(0,0,0,0.10)',
        },
    },

    cardExpanded: {
        boxShadow: '0 6px 24px rgba(0,0,0,0.13)',
    },

    // ── Card header (always visible) ────────────────────────────────────
    cardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 16px',
    },

    // Left part: badge + label
    cardLeft: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0',
        minWidth: 0,
        gap: 4,
    },

    labelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        marginTop: 2,
    },

    typeBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        fontFamily: theme.font.primary,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        flexShrink: 0,
        opacity: 0.9,
    },

    eventLabel: {
        fontFamily: theme.font.primary,
        fontSize: 12,
        fontWeight: 500,
        color: theme.colors.text_subtle_purple,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        letterSpacing: '0.01em',
    },

    descriptionMain: {
        fontFamily: theme.font.primary,
        fontSize: 15,
        fontWeight: 600,
        color: theme.colors.text_heading,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
        wordBreak: 'break-word',
    },

    descriptionMainTruncated: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },

    // Right part: time chip + chevron
    cardRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        flexShrink: 0,
        gap: 6,
    },

    timeChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        fontFamily: 'monospace',
        backgroundColor: theme.colors.grey_light,
        color: theme.colors.text_heading,
        whiteSpace: 'nowrap',
    },

    chevron: {
        fontSize: 16,
        color: theme.isDark ? theme.colors.text_subtle_purple : theme.colors.grey_darker,
        fontWeight: 700,
        lineHeight: 1,
        transition: 'transform 0.2s ease',
        userSelect: 'none',
    },

    chevronOpen: {
        transform: 'rotate(180deg)',
    },

    // ── Expanded body ───────────────────────────────────────────────────
    expandedBody: {
        padding: '0 16px 14px 16px',
        borderTop: `1px solid ${theme.colors.grey_darker}${theme.isDark ? '88' : '33'}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },

    expandedBodyTitle: {
        fontFamily: theme.font.primary,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.colors.text_subtle_purple,
        marginBottom: 4,
        marginTop: 8,
    },

    descriptionFull: {
        fontFamily: theme.font.secondary ?? theme.font.primary,
        fontSize: 13,
        color: theme.colors.text_heading,
        lineHeight: 1.5,
        marginBottom: 8,
        whiteSpace: 'pre-wrap',
    },

    descriptionPreview: {},

    kvGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(100px, 180px) 1fr',
        gap: '4px 12px',
    },

    kvKey: {
        fontFamily: theme.font.primary,
        fontSize: 12,
        fontWeight: 600,
        color: theme.colors.text_subtle_purple,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    kvValue: {
        fontFamily: 'monospace',
        fontSize: 12,
        color: theme.colors.text_heading,
        wordBreak: 'break-word',
    },
}));
